import styled from "@emotion/styled";
import { Settings } from "@mui/icons-material";
import Info from "@mui/icons-material/Info";
import { Box, Button as MuiButton, Tooltip } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "components/Button";
import { SwitchInput } from "components/HookFormComponents/SwitchInput";
import { Fragment, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { connect } from "react-redux";
import { AlertPoliciesActioner } from "services/alertFactory/alertPolicies/actioner";
import Abilities from "services/permissions/abilities";
import {
  filterValidOutputs,
  filterValidPolicies,
  getAllowedOutputTypes,
} from "./utils";

const AlertsManagementComponent = ({ alertPolicies, manageOutputs }) => {
  const [open, setOpen] = useState(false);
  const intl = useIntl();

  const canConfigureOutputs = Abilities.ability.can(
    "read",
    "tabs.alerts.config_outputs"
  );

  const allowedOutputTypes = getAllowedOutputTypes();

  const defaultValues = useMemo(
    () => ({
      policies: filterValidPolicies(alertPolicies, allowedOutputTypes).map(
        (policy) => ({
          id: policy.id,
          outputs: policy.outputs.map((output) => ({
            type: output.type,
            userEnabled: output.userEnabled,
          })),
        })
      ),
    }),
    [alertPolicies]
  );

  const formMethods = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (!formMethods.formState.isDirty && !open) {
      formMethods.reset(defaultValues);
    }
  }, [defaultValues, alertPolicies]);

  const submit = (data) => {
    // Handle form submission logic here
    manageOutputs(data).then(() => {
      setOpen(false);
    });
  };

  if (!alertPolicies || alertPolicies.length === 0 || !canConfigureOutputs) {
    return null;
  }

  return (
    <Fragment>
      <Button
        startIcon={<Settings />}
        fullWidth={false}
        sx={{
          "& .MuiButton-startIcon": { marginRight: "0px", marginLeft: "0px" },
        }}
        onClick={() => setOpen(true)}
      />
      <StyledDialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="alert-management-dialog"
      >
        <StyledDialogTitle id="alert-management-dialog">
          <FormattedMessage id="alertsScene.managementModal.title" />
        </StyledDialogTitle>
        <StyledDialogContent>
          <FormProvider {...formMethods}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              component="form"
              onSubmit={formMethods.handleSubmit(submit)}
              id="alert-management-form"
            >
              {filterValidPolicies(alertPolicies, allowedOutputTypes).map(
                (policy, policyIndex) => (
                  <Box key={policy.id}>
                    <Box
                      sx={{
                        display: "flex",
                        minHeight: "40px",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: "1.2rem",
                          marginBottom: "4px",
                          color: policy.enabled ? "inherit" : "gray",
                        }}
                      >
                        {policy.name}
                      </Box>

                      {!policy.enabled && (
                        <Tooltip
                          key={policy.id}
                          title={
                            !policy.enabled
                              ? intl.formatMessage({
                                  id: "alertsScene.managementModal.policyIsDisabled",
                                })
                              : ""
                          }
                        >
                          <Info sx={{ width: 20, height: 20, color: "gray" }} />
                        </Tooltip>
                      )}
                    </Box>
                    <input
                      type="hidden"
                      {...formMethods.register(`policies.${policyIndex}.id`)}
                      value={policy.id}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {filterValidOutputs(
                        policy.outputs,
                        allowedOutputTypes
                      ).map((output, outputIndex) => (
                        <div key={output.type}>
                          <Tooltip
                            title={
                              !policy.enabled
                                ? intl.formatMessage({
                                    id: "alertsScene.managementModal.policyIsDisabled",
                                  })
                                : !output.enabled
                                ? intl.formatMessage({
                                    id: "alertsScene.managementModal.outputIsDisabled",
                                  })
                                : ""
                            }
                          >
                            <span>
                              <SwitchInput
                                name={`policies.${policyIndex}.outputs.${outputIndex}.userEnabled`}
                                label={intl.formatMessage({
                                  id: `alertsScene.managementModal.enable${output.type.toUpperCase()}`,
                                })}
                                disabled={!output.enabled || !policy.enabled}
                              />
                            </span>
                          </Tooltip>

                          <input
                            type="hidden"
                            {...formMethods.register(
                              `policies.${policyIndex}.outputs.${outputIndex}.type`
                            )}
                            value={output.type}
                          />
                        </div>
                      ))}
                    </Box>
                  </Box>
                )
              )}
            </Box>
          </FormProvider>
        </StyledDialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => setOpen(false)}
            fullWidth={false}
            color="secondary"
            variant="text"
          >
            <FormattedMessage id="components.buttons.cancel" />
          </MuiButton>
          <MuiButton
            fullWidth={false}
            color="primary"
            variant="text"
            type="submit"
            form="alert-management-form"
          >
            <FormattedMessage id="components.buttons.ok" />
          </MuiButton>
        </DialogActions>
      </StyledDialog>
    </Fragment>
  );
};

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    max-width: 600px;
    width: 100%;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  font-weight: bold;
`;

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const mapStateToProps = (state) => {
  return {
    alertPolicies: state.AlertFactory.alertPolicies.data?.data ?? [],
  };
};

const mapDispatchToProps = (dispatch) => ({
  manageOutputs: (data) => dispatch(AlertPoliciesActioner.manageOutputs(data)),
});

export const AlertsManagement = connect(
  mapStateToProps,
  mapDispatchToProps
)(AlertsManagementComponent);
